/**
 * LUZActive "Ask AI" shopping assistant — front-end widget.
 *
 * Loaded with `defer` from layout/theme.liquid, same pattern as the other
 * theme scripts (cart-drawer.js, gallery-zoom.js). Does nothing on first
 * paint beyond wiring click handlers — no layout is read/written until the
 * shopper actually opens the drawer, so it cannot affect LCP/CLS.
 *
 * Reads page context from a JSON script tag the section already renders
 * server-side:
 *   - #product-ai-context on PDPs (sections/main-product.liquid)
 *   - #store-ai-context everywhere else (sections/header.liquid), a much
 *     smaller store-policy blob
 *
 */
const ENDPOINT = "https://luzactive-ai-assistant.luzactive-ai.workers.dev/chat";

(function () {
  "use strict";

  if (window.LuzAIAssistant) return; // don't double-init if included twice
  window.LuzAIAssistant = { open, close };

  var state = {
    open: false,
    sending: false,
    history: [], // [{role, content}], kept short client-side too
    context: null,
    page: null,
  };

  document.addEventListener("DOMContentLoaded", init);
  if (document.readyState === "complete" || document.readyState === "interactive") init();

  function init() {
    if (window.__luzAIInit) return;
    window.__luzAIInit = true;

    state.context = readJSON("product-ai-context") || readJSON("store-ai-context");
    state.page = {
      type: document.body.getAttribute("data-page-type") || inferPageType(),
      url: window.location.pathname,
      title: document.title,
    };

    renderTriggers();
    wireDrawerChrome();
  }

  function inferPageType() {
    var cls = document.body.className || "";
    if (cls.indexOf("template-product") !== -1) return "product";
    if (cls.indexOf("template-search") !== -1) return "search";
    if (cls.indexOf("template-collection") !== -1) return "collection";
    return "other";
  }

  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // -------------------------------------------------------------------
  // Trigger placement — a bar near the buy box on PDP, a compact chip
  // next to the search bar everywhere else (header.liquid renders the
  // mount points; we only fill them in and attach behaviour here).
  // -------------------------------------------------------------------

  function renderTriggers() {
    var pdpMount = document.querySelector("[data-ai-trigger-pdp]");
    if (pdpMount) {
      pdpMount.innerHTML =
        '<button type="button" class="ai-trigger-bar" data-ai-open>' +
        '<span class="ai-trigger-bar__icon" aria-hidden="true">✦</span>' +
        '<span>Ask AI about this product…</span>' +
        "</button>" +
        renderChips(defaultChipsFor(state.page.type, state.context));
      pdpMount.querySelectorAll("[data-ai-chip]").forEach(function (chip) {
        chip.addEventListener("click", function () {
          open();
          send(chip.getAttribute("data-ai-chip"));
        });
      });
    }

    // Two mount points share this attribute — the desktop header search bar
    // and the mobile drawer's own search bar — so every match must be
    // filled, not just the first (querySelector would silently leave the
    // mobile drawer's icon missing while its input padding still reserves
    // space for it).
    var searchMounts = document.querySelectorAll("[data-ai-trigger-search]");
    searchMounts.forEach(function (searchMount) {
      // Icon-only button living inside the search input itself (see
      // .search-bar [data-ai-trigger-search] in base.css) rather than a
      // separate pill sitting beside the search bar.
      searchMount.innerHTML =
        '<button type="button" class="ai-search-icon" data-ai-open aria-label="Ask AI about products">' +
        '<span aria-hidden="true">✦</span>' +
        "</button>";
    });

    // Floating badge as a fallback affordance on pages with neither mount
    // (e.g. collection pages) so the assistant is still reachable.
    if (!pdpMount) {
      var fab = document.createElement("button");
      fab.type = "button";
      fab.className = "ai-fab";
      fab.setAttribute("data-ai-open", "");
      fab.setAttribute("aria-label", "Ask AI");
      fab.innerHTML = '<span aria-hidden="true">✦</span>';
      document.body.appendChild(fab);
    }

    document.querySelectorAll("[data-ai-open]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        open();
      });
    });
  }

  function defaultChipsFor(pageType, context) {
    if (pageType === "product" && context) {
      var chips = ["How does sizing run?", "What's the return policy?", "How do I clean these?"];
      if (context.is_exclusive) chips[1] = "Is this a final sale item?";
      return chips;
    }
    return ["What's your return policy?", "How fast is shipping?", "Help me find running shoes"];
  }

  function renderChips(chips) {
    if (!chips || !chips.length) return "";
    return (
      '<div class="ai-chips">' +
      chips
        .map(function (c) {
          return '<button type="button" class="ai-chip" data-ai-chip="' + escapeAttr(c) + '">' + escapeHtml(c) + "</button>";
        })
        .join("") +
      "</div>"
    );
  }

  // -------------------------------------------------------------------
  // Drawer chrome — built once, lazily, the first time it's needed.
  // -------------------------------------------------------------------

  var drawerBuilt = false;

  function wireDrawerChrome() {
    var drawer = document.getElementById("AIAssistantDrawer");
    if (!drawer) return; // snippet not rendered on this template — nothing to wire

    drawer.querySelector("[data-ai-close]").addEventListener("click", close);
    drawer.querySelector("[data-ai-backdrop]").addEventListener("click", close);
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && state.open) close();
    });

    var form = drawer.querySelector("[data-ai-form]");
    var input = drawer.querySelector("[data-ai-input]");
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var text = input.value.trim();
      if (!text) return;
      input.value = "";
      send(text);
    });

    drawerBuilt = true;
  }

  function open() {
    var drawer = document.getElementById("AIAssistantDrawer");
    if (!drawer) return;
    state.open = true;
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    document.body.classList.add("ai-drawer-open");

    if (!drawer.dataset.greeted) {
      drawer.dataset.greeted = "1";
      appendMessage("assistant", greetingFor(state.page.type, state.context));
      var chipsMount = drawer.querySelector("[data-ai-drawer-chips]");
      if (chipsMount) {
        chipsMount.innerHTML = renderChips(defaultChipsFor(state.page.type, state.context));
        chipsMount.querySelectorAll("[data-ai-chip]").forEach(function (chip) {
          chip.addEventListener("click", function () {
            send(chip.getAttribute("data-ai-chip"));
          });
        });
      }
    }

    setTimeout(function () {
      var input = drawer.querySelector("[data-ai-input]");
      if (input) input.focus();
    }, 50);
  }

  function close() {
    var drawer = document.getElementById("AIAssistantDrawer");
    if (!drawer) return;
    state.open = false;
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    document.body.classList.remove("ai-drawer-open");
  }

  function greetingFor(pageType, context) {
    if (pageType === "product" && context && context.title) {
      return "Hi! I can help with sizing, materials, shipping or returns for the " + context.title + ". What would you like to know?";
    }
    return "Hi! Ask me about sizing, shipping, returns, or finding the right product.";
  }

  // -------------------------------------------------------------------
  // Messaging — streams the reply in as it arrives for a live "typing"
  // feel, and degrades gracefully if the endpoint isn't reachable yet
  // (e.g. before the Worker URL has been set).
  // -------------------------------------------------------------------

  function appendMessage(role, text) {
    var drawer = document.getElementById("AIAssistantDrawer");
    var list = drawer.querySelector("[data-ai-messages]");
    var row = document.createElement("div");
    row.className = "ai-msg ai-msg--" + role;
    var bubble = document.createElement("div");
    bubble.className = "ai-msg__bubble";
    bubble.textContent = text || "";
    row.appendChild(bubble);
    list.appendChild(row);
    list.scrollTop = list.scrollHeight;
    return bubble;
  }

  function send(text) {
    if (state.sending) return;
    if (ENDPOINT.indexOf("REPLACE-ME") !== -1) {
      appendMessage("user", text);
      appendMessage(
        "assistant",
        "The assistant's backend hasn't been connected yet — once the Cloudflare Worker is deployed and its URL is set in ai-assistant.js, I'll be able to answer this."
      );
      return;
    }

    state.sending = true;
    appendMessage("user", text);
    var bubble = appendMessage("assistant", "");
    bubble.classList.add("is-typing");

    var payload = {
      message: text,
      history: state.history,
      context: state.context,
      page: state.page,
    };

    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        if (!res.ok || !res.body) throw new Error("bad response " + res.status);
        return streamInto(res.body, bubble);
      })
      .then(function (fullText) {
        bubble.classList.remove("is-typing");
        state.history.push({ role: "user", content: text });
        state.history.push({ role: "assistant", content: fullText });
        state.history = state.history.slice(-12);
      })
      .catch(function () {
        bubble.classList.remove("is-typing");
        bubble.textContent = "Sorry, I couldn't reach the assistant just now. Please try again in a moment.";
      })
      .finally(function () {
        state.sending = false;
      });
  }

  function streamInto(body, bubble) {
    var reader = body.getReader();
    var decoder = new TextDecoder();
    var buffer = "";
    var full = "";
    var list = document.querySelector("#AIAssistantDrawer [data-ai-messages]");

    function pump() {
      return reader.read().then(function (result) {
        if (result.done) return full;
        buffer += decoder.decode(result.value, { stream: true });
        var lines = buffer.split("\n");
        buffer = lines.pop();
        lines.forEach(function (line) {
          if (!line.trim()) return;
          var obj;
          try {
            obj = JSON.parse(line);
          } catch (e) {
            return;
          }
          if (obj.type === "token") {
            full += obj.text;
            bubble.textContent = full;
            if (list) list.scrollTop = list.scrollHeight;
          } else if (obj.type === "error") {
            full = obj.message || "Something went wrong.";
            bubble.textContent = full;
          }
        });
        return pump();
      });
    }
    return pump();
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function escapeAttr(s) {
    return escapeHtml(s);
  }
})();
